<template>
    <div class="row">
        <div class="position-relative w-100">
            <transition-group name="fade" mode="in-out">
                <div class="row pt-2" :key="'data-chart'">
                    <div class="col-12">
                        <goals-chart
                            :goal="goal"
                            :trackers="trackers"
                            :customization="customization"
                        ></goals-chart>
                    </div>
                </div>

                <div class="row pt-2" :key="'rename-axes'">
                    <div class="col-12">
                        <goals-rename-axes v-if="canAddEditGoals" :goal="goal"></goals-rename-axes>
                    </div>
                </div>

                <div
                    v-if="!['preference_assessment'].includes(goal.tracker_type)"
                    class="row pt-2"
                    :key="'phase-change'"
                >
                    <div class="col-12">
                        <goals-phase-change
                            v-if="canAddEditGoals"
                            :user-id="userId"
                            :goal="goal"
                            @goal-updated="$emit('goal-updated')"
                        ></goals-phase-change>
                    </div>
                </div>

                <div
                    v-if="goal.tracker_type === 'preference_assessment'"
                    class="row pt-2"
                    :key="'goal-details-pa'"
                >
                    <div class="col-12 col-sm-9 p-1 p-sm-2">
                        <goals-description
                            @refresh-goals="$emit('refresh-goals')"
                            :goal="goal"
                        ></goals-description>
                    </div>
                    <div class="col-6 col-sm-3 col-sm-3 p-1 p-sm-2">
                        <goals-progress-preference-assessment
                            :goal="goal"
                            :trackers="trackers"
                        ></goals-progress-preference-assessment>
                    </div>
                </div>
                <div
                    v-else-if="goal.tracker_type === 'task_analysis'"
                    class="row pt-2"
                    :key="'goal-details-ta'"
                >
                    <div class="col-12 p-1">
                        <goals-description-task-analysis
                            :stats="stats"
                            :user-can-edit-goal="canAddEditGoals"
                            @refresh-goals="$emit('refresh-goals')"
                            :goal="goal"
                        ></goals-description-task-analysis>
                    </div>
                </div>
                <div v-else="" class="row pt-2" :key="'goal-details'">
                    <div
                        class="d-none d-sm-block col-sm-3 p-1 p-sm-2 d-flex justify-content-between"
                    >
                        <goals-average-score
                            :goal="goal"
                            :title="'Progress average'"
                            :progress="helpers._get(goal, 'stats.progress_average', 0)"
                            :show-info="true"
                        ></goals-average-score>
                    </div>
                    <div class="col-12 col-sm-6 p-1 p-sm-2">
                        <goals-description
                            :user-can-edit-goal="canAddEditGoals"
                            @refresh-goals="$emit('refresh-goals')"
                            :goal="goal"
                        ></goals-description>
                    </div>
                    <div
                        v-if="goal.tracker_type === 'multi_tally'"
                        class="col-6 col-sm-3 col-sm-3 p-1 p-sm-2"
                    >
                        <goals-progress-multi-tally
                            :goal="goal"
                            :trackers="trackers"
                        ></goals-progress-multi-tally>
                    </div>
                    <div v-else="" class="col-6 col-sm-3 col-sm-3 p-1 p-sm-2">
                        <goals-progress :goal="goal" :stats="stats"></goals-progress>
                    </div>
                </div>

                <div class="row pt-2" :key="'implementation-homework'">
                    <div class="col-12">
                        <goals-instructions
                            :goal="goal"
                            :can-edit="canAddEditGoals"
                            @goal-updated="$emit('goal-updated')"
                        ></goals-instructions>
                    </div>
                </div>

                <div :key="'data-new-list'" class="scroll-container">
                    <div class="row pt-2" :key="'data-new'">
                        <div class="col-12">
                            <goals-new-data
                                @refresh-goals="$emit('refresh-goals')"
                                @refresh-trackers="getTrackers()"
                                :key="`goal-${goal.id}-new`"
                                :goal="goal"
                                :customization="customization"
                            ></goals-new-data>
                        </div>
                    </div>

                    <div class="row mb-5" :key="'data-list'" v-if="trackers.length > 0">
                        <div v-for="(t, i) in trackers" class="col-12">
                            <goals-data
                                @refresh-trackers="getTrackers()"
                                @edit-target="setEditTarget($event)"
                                v-if="!(editTarget && editTarget.id === t.id)"
                                :customization="customization"
                                :is-baseline="i === trackers.length - 1"
                                :key="`g${goal.id}-t${t.id}-r${parseInt(1e6 * Math.random())}`"
                                :target="t"
                                :patient-id="goal.patient.id"
                                :user-id="userId"
                                :goal="goal"
                                :success="goal.parameters.success"
                                :type="goal.parameters.type"
                                :editTarget="editTarget"
                                :successThreshold="goal.parameters.success"
                                :attemptThreshold="goal.parameters.attempts"
                            ></goals-data>
                            <goals-new-data
                                v-if="editTarget && editTarget.id === t.id"
                                :key="`goal-${goal.id}-edit`"
                                @refresh-goals="$emit('refresh-goals')"
                                @refresh-trackers="getTrackers()"
                                @edit-done="editTarget = null"
                                :customization="customization"
                                :edit-target="editTarget"
                                :goal="goal"
                            ></goals-new-data>
                        </div>
                    </div>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import GoalsAverageScore from "./GoalsAverageScore";
import GoalsDescription from "./GoalsDescription";
import GoalsDescriptionTaskAnalysis from "./GoalsDescriptionTaskAnalysis";
import GoalsProgress from "./GoalsProgress";
import GoalsProgressMultiTally from "./GoalsProgressMultiTally";
import GoalsInstructions from "./GoalsInstructions";
import GoalsNewData from "./GoalsNewData";
import GoalsData from "./GoalsData";
import GoalsChart from "./GoalsChart";
import GoalsProgressPreferenceAssessment from "./GoalsProgressPreferenceAssessment";
import GoalsRenameAxes from "./GoalsRenameAxes";
import GoalsPhaseChange from "./GoalsPhaseChange";
import { permissions } from "../../plugins/permissions";
import { helpers } from "../../plugins/helpers";

export default {
    name: "Detail",
    props: ["goal", "customizations", "userId"],
    mixins: [permissions],
    components: {
        GoalsAverageScore,
        GoalsDescription,
        GoalsDescriptionTaskAnalysis,
        GoalsProgress,
        GoalsProgressMultiTally,
        GoalsInstructions,
        GoalsNewData,
        GoalsData,
        GoalsChart,
        GoalsRenameAxes,
        GoalsPhaseChange,
        GoalsProgressPreferenceAssessment,
    },

    data() {
        return {
            helpers,
            data: [],
            trackers: [],
            stats: this.goal.stats,
            editTarget: null,
        };
    },
    mounted() {
        this.getTrackers();
    },
    computed: {
        customization() {
            return this.customizations.find(c => c.type === this.goal.tracker_type);
        },
    },
    watch: {
        goal() {
            if (this.goal) {
                this.getTrackers();
            }
        },
        trackers() {
            this.getStats();
        },
    },
    methods: {
        getTrackers() {
            let patientId = _.get(this.goal, "patient.id", null);
            let goalId = _.get(this.goal, "id", null);
            if (patientId && goalId) {
                axios
                    .get(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`, {
                        params: {
                            sort: [
                                {
                                    key: "session_at",
                                    direction: "DESC",
                                },
                            ],
                        },
                    })
                    .then(response => {
                        this.trackers = response.data.data;
                    });
            }
        },

        getStats() {
            axios
                .get(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`)
                .then(response => {
                    this.stats = response.data.data.stats;
                    if (["preference_assessment", "cme"].includes(this.goal.tracker_type)) {
                        this.stats.progress_average = "N/A";
                    }
                });
        },
        targetDetails(target) {
            this.target = target;
        },

        setEditTarget(target) {
            let editTarget = target;
            editTarget.session_at = new Date(editTarget.session_at);
            this.$set(this, "editTarget", editTarget);
        },
    },
};
</script>

<style scoped lang="scss">
.filter {
    font-size: 0.5rem;
    font-weight: 500;
    border-radius: 0;
    background-color: #fcfcfc;
    color: #aeaeae;
}

@media screen and (min-width: 576px) {
    .filter {
        font-size: 0.7rem;
    }
}

@media screen and (max-width: 992px) {
    .scroll-container {
        overflow-x: scroll;
        > .row {
            min-width: 768px !important;
        }
    }
}
</style>
